/**
 * Date utility functions for the roadmap board.
 * All dates are ISO strings (YYYY-MM-DD).
 * Business days = Mon-Fri (no holidays).
 */

// Board reference date: Monday of KW 3, 2026
export const BOARD_START_DATE = "2026-01-12";

const DAYS_PER_WEEK = 5;

/**
 * Parse an ISO date string to a Date object (local time, noon to avoid DST issues).
 */
function parseDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0);
}

/**
 * Format a Date object to ISO date string (YYYY-MM-DD).
 */
function toISODateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Get day of week: 0=Mon, 1=Tue, ..., 4=Fri, 5=Sat, 6=Sun.
 */
function getWeekday(date: Date): number {
  const jsDay = date.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  return jsDay === 0 ? 6 : jsDay - 1; // Convert to 0=Mon, ..., 6=Sun
}

/**
 * Convert an ISO date string to a business-day index relative to a board start date.
 * Only counts Mon-Fri days.
 * Can return negative values if date is before the board start date.
 */
export function dateToBusinessDayIndex(dateStr: string, boardStartDate: string = BOARD_START_DATE): number {
  const target = parseDate(dateStr);
  const start = parseDate(boardStartDate);

  const msPerDay = 86400000;
  const calendarDays = Math.round((target.getTime() - start.getTime()) / msPerDay);

  if (calendarDays === 0) return 0;

  // Always count forward from the earlier date to the later date
  const earlier = calendarDays > 0 ? start : target;
  const absCalDays = Math.abs(calendarDays);

  const fullWeeks = Math.floor(absCalDays / 7);
  const remainder = absCalDays % 7;
  let businessDays = fullWeeks * DAYS_PER_WEEK;

  // Count business days in the partial week remainder
  for (let i = 1; i <= remainder; i++) {
    const d = new Date(earlier);
    d.setDate(earlier.getDate() + fullWeeks * 7 + i);
    if (getWeekday(d) < 5) businessDays++;
  }

  return calendarDays > 0 ? businessDays : -businessDays;
}

/**
 * Convert a business-day index (relative to a board start date) to an ISO date string.
 * Uses mathematical approach for efficiency: calculate full weeks, then iterate remainder.
 */
export function businessDayIndexToDate(dayIndex: number, boardStartDate: string = BOARD_START_DATE): string {
  const start = parseDate(boardStartDate);

  if (dayIndex === 0) return boardStartDate;

  const sign = dayIndex >= 0 ? 1 : -1;
  const absDays = Math.abs(dayIndex);

  // Full weeks of business days = absDays / 5
  const fullWeeks = Math.floor(absDays / DAYS_PER_WEEK);
  const remainderBizDays = absDays % DAYS_PER_WEEK;

  // Jump ahead by full weeks (7 calendar days each)
  const current = new Date(start);
  current.setDate(current.getDate() + sign * fullWeeks * 7);

  // Walk remaining business days
  let remaining = remainderBizDays;
  while (remaining > 0) {
    current.setDate(current.getDate() + sign);
    if (getWeekday(current) < 5) {
      remaining--;
    }
  }

  return toISODateString(current);
}

/**
 * Convenience: convert a task's startDate to a day index.
 */
export function getTaskDayIndex(startDate: string, boardStartDate: string = BOARD_START_DATE): number {
  return dateToBusinessDayIndex(startDate, boardStartDate);
}

/**
 * Format a date for German display: "13.01.2026"
 */
export function formatDateDE(dateStr: string): string {
  const d = parseDate(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

/**
 * Get ISO week number and year from a date string.
 * Uses ISO 8601 definition: week starts Monday, week 1 contains Jan 4th.
 */
export function getKwFromDate(dateStr: string): { kw: number; year: number } {
  const date = parseDate(dateStr);

  // ISO week: the week containing Thursday determines the year
  const thursday = new Date(date);
  thursday.setDate(date.getDate() + (3 - getWeekday(date)));

  const year = thursday.getFullYear();
  const jan1 = new Date(year, 0, 1, 12, 0, 0);

  const msPerDay = 86400000;
  const dayOfYear = Math.round((thursday.getTime() - jan1.getTime()) / msPerDay) + 1;
  const kw = Math.ceil(dayOfYear / 7);

  return { kw, year };
}

/**
 * Return the next business day (Mon-Fri). If already a business day, return same date.
 */
export function nextBusinessDay(dateStr: string): string {
  const d = parseDate(dateStr);
  const wd = getWeekday(d);
  if (wd < 5) return dateStr;
  d.setDate(d.getDate() + (wd === 5 ? 2 : 1));
  return toISODateString(d);
}

/**
 * Add N business days to a date. N can be negative.
 */
export function addBusinessDays(dateStr: string, days: number, boardStartDate: string = BOARD_START_DATE): string {
  const dayIndex = dateToBusinessDayIndex(dateStr, boardStartDate);
  return businessDayIndexToDate(dayIndex + days, boardStartDate);
}

/**
 * Extract month (0-indexed) and year from an ISO date string.
 */
export function getMonthAndYear(dateStr: string): { month: number; year: number } {
  const [y, m] = dateStr.split('-').map(Number);
  return { month: m - 1, year: y };
}

/**
 * Format an ISO timestamp as a compact relative string (e.g. "5m", "3h", "2d", "1mo").
 */
export function relativeDate(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}d`;
  const diffMo = Math.floor(diffD / 30);
  return `${diffMo}mo`;
}
